import {
  FormField,
  Input,
  Box,
  Container,
  Header,
  SpaceBetween,
  Button,
  Form,
  ColumnLayout,
  Spinner,
  Modal,
  Badge,
  Flashbar,
} from '@amzn/awsui-components-console';
import { useEffect, useState } from 'react';
import {
  LaunchTask,
  WorkStatus,
  UserInfo,
  DeleteLaunchTaskCommand,
  DeleteLaunchTaskCommandOutput,
  CreateLaunchInput,
  CreateLaunchTaskCommandOutput,
  CreateLaunchTaskCommand,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../../../authentication/context';
import { callApi } from '../../../client';
import { NoTokenError } from '../../../errors/NoTokenError';
import { useApiErrors } from '../../../components/ApiErrorContext';

import { v4 } from 'uuid';

interface BuddyLaunchesProps {
  buddyLaunches: LaunchTask[];
  launchTaskDetail: LaunchTask;
  buddyLaunchesLoading: boolean;
  buddyLaunchFetchSuccess: boolean;
}

// Fallback "Launch status" for a new buddy launch when the parent task has no status.
// `status` is @required + @length(min: 3) on CreateLaunchInput, but it is optional on the
// LaunchTask output: parent tasks from WALKMAN deprecation / unmatched launches can carry a
// null status (only documentationStatus is coerced on the backend, not status). Falling back
// to '' would send an invalid request (min: 3) while the Add button stays enabled, so we use a
// valid 3-char placeholder instead. A buddy inherits the parent's status when it is present.
const BUDDY_LAUNCH_FALLBACK_STATUS = 'N/A';

export const EditBuddyLaunchPage = ({
  launchTaskDetail,
  buddyLaunches,
  buddyLaunchesLoading,
  buddyLaunchFetchSuccess,
}: BuddyLaunchesProps) => {
  const [buddyLaunchName, setBuddyLaunchName] = useState('');
  const [buddyLaunchWriter, setBuddyLaunchWriter] = useState('');

  const [buddyLaunchesTaskList, setBuddyLaunchesTaskList] =
    useState<LaunchTask[]>(buddyLaunches);

  const [newEntryLoading, setNewEntryLoading] = useState(false);
  const [fetchSuccess, setFetchSuccess] = useState(true);

  const [deleteLoading, setDeleteLoading] = useState(true);
  const [deleteSuccess, setDeleteSuccess] = useState(true);

  const [enableDeleteButton, setEnableDeleteButton] = useState(true);
  const [deleteTaskName, setDeleteTaskName] = useState('');

  const { isAuthenticated, token, packageInfoPermissionsGroup } =
    useAuthState();
  const { pushError } = useApiErrors();

  const [disableAddBuddyLaunchButton, setDisableAddBuddyLaunchButton] =
    useState(false);

  const [writerSectionErrorIndicator, setWriterSectionErrorIndicator] =
    useState('');
  const [taskNameSectionErrorIndicator, setTaskNameSectionErrorIndicator] =
    useState('');

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [errorMsg, SetErrorMsg] = useState('');

  const [addSuccess, setAddSuccess] = useState(false);
  const [deleteSuccessMsg, setDeleteSuccessMsg] = useState<string | null>(null);

  const [selectedTask, setSelectedTask] = useState<LaunchTask>();

  const addBuddy = (task: LaunchTask) => {
    setBuddyLaunchesTaskList([...buddyLaunchesTaskList, task]);
  };

  const deleteBuddy = (buddyLaunch: LaunchTask) => {
    const updatedItems = [...buddyLaunchesTaskList].filter(
      (task) => task !== buddyLaunch
    );
    setBuddyLaunchesTaskList(updatedItems);
  };

  async function callDeleteLaunchTaskApi(buddyLaunch?: LaunchTask) {
    if (!buddyLaunch || !buddyLaunch.launchId) {
      setDeleteLoading(false);
      setEnableDeleteButton(true);
      setDeleteSuccess(false);
      return;
    }
    if (!token) throw new NoTokenError();

    try {
      const response: DeleteLaunchTaskCommandOutput = await callApi(
        new DeleteLaunchTaskCommand({
          launchId: buddyLaunch.launchId,
        }),
        token
      );
      setDeleteLoading(false);
      setEnableDeleteButton(true);
      setDeleteTaskName(buddyLaunch.launchId + ' ' + buddyLaunch.name);
      if (response.$metadata.httpStatusCode == 200) {
        deleteBuddy(buddyLaunch);
        setDeleteSuccessMsg(
          `delete buddy launch ${buddyLaunch.launchId} complete`
        );
      }
    } catch (err: any) {
      setDeleteLoading(false);
      setEnableDeleteButton(true);
      setDeleteTaskName(buddyLaunch.launchId + ' ' + buddyLaunch.name);
      pushError(
        'Delete buddy launch failed',
        err?.message || 'Failed to delete buddy launch'
      );
    }
  }

  async function callCreateLaunchTaskApi(newLaunchTask: CreateLaunchInput) {
    if (!token) throw new NoTokenError();
    try {
      const response: CreateLaunchTaskCommandOutput = await callApi(
        new CreateLaunchTaskCommand({
          task: newLaunchTask,
        }),
        token
      );
      setNewEntryLoading(false);
      setAddSuccess(true);

      addBuddy(response.task);
    } catch (err: any) {
      setNewEntryLoading(false);
      pushError(
        'Add buddy launch failed',
        err?.message || 'Failed to create buddy launch'
      );
      console.log('error with creating buddy launch', err);
    }
  }

  function removeDuplicates(arr: LaunchTask[]) {
    const uniqueTasks = new Map<string, LaunchTask>();
    arr.forEach((task) => {
      uniqueTasks.set(task.launchId, task);
    });
    return Array.from(uniqueTasks.values());
  }

  useEffect(() => {
    setBuddyLaunchesTaskList(removeDuplicates(buddyLaunches));
  }, [buddyLaunches]);

  useEffect(() => {
    if (buddyLaunchWriter) {
      const allLetters = /^[A-Za-z0-9]+$/;
      const invalidChars = /[@#$%^&*]/;
      if (
        !allLetters.exec(buddyLaunchWriter) ||
        invalidChars.exec(buddyLaunchWriter)
      ) {
        setWriterSectionErrorIndicator('Alias can only take letters or number');
      } else if (buddyLaunchWriter.includes(' ')) {
        setWriterSectionErrorIndicator('Alias cannot have space in between');
      } else {
        setWriterSectionErrorIndicator('');
      }
    } else {
      setWriterSectionErrorIndicator('');
    }
  }, [buddyLaunchWriter]);

  useEffect(() => {
    if (buddyLaunchName) {
      const invalidChars = /[@#/*]/;
      if (invalidChars.exec(buddyLaunchName)) {
        setTaskNameSectionErrorIndicator(
          'Please do not include @#/* in task name!'
        );
      } else if (buddyLaunchWriter.includes(' ')) {
        setTaskNameSectionErrorIndicator(
          'Please do not include space in task name!'
        );
      } else if (
        buddyLaunches.find((task) => {
          return task.name === buddyLaunchName;
        })
      ) {
        setTaskNameSectionErrorIndicator('Duplicate task name!');
      } else {
        setTaskNameSectionErrorIndicator('');
      }
    } else {
      setTaskNameSectionErrorIndicator('');
    }
  }, [buddyLaunchName]);

  useEffect(() => {
    if (!buddyLaunchName || !buddyLaunchWriter) {
      setDisableAddBuddyLaunchButton(true);
    } else {
      setDisableAddBuddyLaunchButton(false);
    }
  }, [buddyLaunchWriter, buddyLaunchName]);

  if (!buddyLaunchFetchSuccess) {
    return <Badge color='red'>Error</Badge>;
  }

  if (buddyLaunchesLoading) {
    return <Spinner size='large' />;
  }

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <SpaceBetween direction='vertical' size='l'>
        {addSuccess && (
          <Flashbar
            items={[
              {
                header: 'Success',
                type: 'success',
                content: 'new buddy launch update complete',
                dismissible: true,
                dismissLabel: 'Dismiss',
                onDismiss: () => setAddSuccess(false),
                id: 'add-success',
              },
            ]}
          />
        )}
        {deleteSuccessMsg && (
          <Flashbar
            items={[
              {
                header: 'Success',
                type: 'success',
                content: deleteSuccessMsg,
                dismissible: true,
                dismissLabel: 'Dismiss',
                onDismiss: () => setDeleteSuccessMsg(null),
                id: 'delete-success',
              },
            ]}
          />
        )}
        <Form>
          <Container header={<Header variant='h2'>Buddy launches</Header>}>
            <SpaceBetween direction='vertical' size='l'>
              <Box display='block'>
                {buddyLaunchesTaskList.map((buddyLaunch, i) => (
                  <div key={buddyLaunch.name}>
                    <Container>
                      <Box display='block'>
                        <ColumnLayout columns={4}>
                          <div>
                            <FormField
                              label='Buddy launch ID'
                              errorText={errorMsg}
                            >
                              <Input
                                disabled={true}
                                value={buddyLaunch.launchId}
                              />
                            </FormField>
                          </div>
                          <div>
                            <FormField label='Buddy launch name'>
                              <Input
                                disabled={true}
                                value={buddyLaunch.name ?? ''}
                              />
                            </FormField>
                          </div>
                          <div>
                            <FormField label='Assignee'>
                              <Input
                                disabled={true}
                                value={
                                  buddyLaunch?.writerAssignment?.alias ?? ''
                                }
                              />
                            </FormField>
                          </div>
                          <div>
                            <FormField label='Action'>
                              <Button
                                iconName='status-negative'
                                variant='link'
                                disabled={
                                  !enableDeleteButton ||
                                  launchTaskDetail.workStatus ===
                                    WorkStatus.DELETED
                                }
                                onClick={() => {
                                  setDeleteModalVisible(true);
                                  setSelectedTask(buddyLaunch);
                                }}
                              >
                                Remove
                              </Button>
                            </FormField>
                          </div>
                        </ColumnLayout>
                      </Box>
                    </Container>
                  </div>
                ))}
              </Box>

              <Modal
                size='medium'
                onDismiss={() => setDeleteModalVisible(false)}
                visible={deleteModalVisible}
                footer={
                  <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                      <Button
                        variant='link'
                        onClick={() => setDeleteModalVisible(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant='primary'
                        onClick={() => {
                          setDeleteModalVisible(false);
                          setEnableDeleteButton(false);
                          setDeleteLoading(true);
                          void callDeleteLaunchTaskApi(selectedTask);
                        }}
                      >
                        Confirm
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                Do you want to delete this task
                <Badge color='red'>{` ${selectedTask?.name}`}</Badge>
              </Modal>

              <Box display={newEntryLoading ? 'block' : 'none'}>
                <Spinner size='large' />
              </Box>

              <Box display={newEntryLoading ? 'none' : 'block'}>
                <Container>
                  <ColumnLayout columns={3}>
                    <div>
                      <FormField
                        label='Buddy launch name'
                        errorText={taskNameSectionErrorIndicator}
                      >
                        <Input
                          disabled={false}
                          value={buddyLaunchName}
                          onChange={({ detail }) => {
                            setBuddyLaunchName(detail.value);
                          }}
                        />
                      </FormField>
                    </div>
                    <div>
                      <FormField
                        label='Assignee'
                        errorText={writerSectionErrorIndicator}
                      >
                        <Input
                          disabled={false}
                          value={buddyLaunchWriter}
                          onChange={({ detail }) => {
                            setBuddyLaunchWriter(detail.value);
                          }}
                        />
                      </FormField>
                    </div>
                    <div>
                      <FormField label='Action'>
                        <Button
                          variant='primary'
                          disabled={
                            disableAddBuddyLaunchButton ||
                            writerSectionErrorIndicator !== '' ||
                            taskNameSectionErrorIndicator !== '' ||
                            launchTaskDetail.workStatus === WorkStatus.DELETED
                          }
                          onClick={() => {
                            if (buddyLaunchName && buddyLaunchWriter) {
                              const newWriter: UserInfo = {
                                alias: buddyLaunchWriter,
                                fullName: '',
                              };
                              const newLaunchTask: CreateLaunchInput = {
                                name: buddyLaunchName,
                                parentTask: launchTaskDetail.launchId,
                                writerAssignment: newWriter,
                                status:
                                  launchTaskDetail.status ??
                                  BUDDY_LAUNCH_FALLBACK_STATUS,
                                type: 'BUDDY',
                                documentationStatus: 'GREEN',
                              };
                              setNewEntryLoading(true);
                              void callCreateLaunchTaskApi(newLaunchTask);
                            }
                            setBuddyLaunchName('');
                            setBuddyLaunchWriter('');
                            setNewEntryLoading(true);
                          }}
                        >
                          Add
                        </Button>
                      </FormField>
                    </div>
                  </ColumnLayout>
                </Container>
              </Box>
            </SpaceBetween>
          </Container>
        </Form>
      </SpaceBetween>
    </form>
  );
};

export default EditBuddyLaunchPage;
